// import * as React from 'react';
import { useEffect } from 'react';
import { inject } from 'mobx-react';
import { setVacFrameVisibilityHidden, VirtualAgentControl } from '@shared-ui/customer-virtual-agent-control';
import { Viewport, ViewSmall, ViewMedium } from '@shared-ui/viewport-context';
import {
  renderVacProps,
  VirtualAgentControlProps,
  floatingVacProps,
  embeddedVacProps,
  VirtualAgentControlAdapter,
  VirtualAgentControl as VirtualAgentControlType,
} from './virtual-agent-control-types';
import { VacConfig, fallbackError, vacEvents, egClickStreamConstants } from 'common/constant';
import { EVENTS } from 'utils/logger-events';
import { prepareClickStreamPayload } from 'common/helper';
import { useEgClickstream, useClickstreamAnalytics } from '@shared-ui/clickstream-analytics-context';
import { Experiment, ExperimentControl, ExperimentVariant, useExperiment } from '@shared-ui/experiment-context';
import { useResourceContext } from 'rm-plugin';
import { ClientLogger } from 'bernie-client';
import loggingConstants from 'common/loggingConstants';
import { useVirtualAgentContext, REQUIRED_EVENT } from '@shared-ui/retail-help-center-util';
import React from 'react';
import { VacOrchestrator } from '@shared-ui/retail-virtual-agent-vac-orchestrator';
import { Chat } from '@shared-ui/retail-virtual-agent-chat';
import { ContactUs } from '@shared-ui/retail-help-center-contact-us-widget';

const { errorEvents, vacHandledErrorEvents, errorEventMap } = VacConfig;
const { vacConversationCreatedEvent } = vacEvents;

const logger = ClientLogger.getLoggerWithIdentifier('virtual-agent-control');

declare const window: Window & {
  VirtualAgentControl: VirtualAgentControlType;
  VacClientAdapter: VirtualAgentControlAdapter;
};
interface Props {
  blankSlateLayout?: boolean;
  userHasTrip?: boolean;
  signedInUser?: boolean;
}

export const RenderVac: React.FC<renderVacProps & Props> = inject('virtualAgentControlStore')(
  ({ config, virtualAgentProps, virtualAgentControlStore, blankSlateLayout, userHasTrip, signedInUser }) => {
    const trackClick = useEgClickstream();
    const { reset } = useClickstreamAnalytics();
    const { exposure, logExperiment } = useExperiment('Help_Center_AA_Match_Rate');
    const { exposure: sharedUIVacExposure } = useExperiment('Shared_UI_VAC_Web_HelpCenter');
    const {
      egClickStreamEventNames,
      egClickStreamEventTypes,
      egClickStreamEventCategories,
      egClickStreamEventVersion,
      egClickStreamEventPageName,
      egClickStreamEventAppName,
    } = egClickStreamConstants;
    const subscribedEvents = Object.values(vacEvents);
    const { resourceContext } = useResourceContext();
    const chatBackData = resourceContext['quickLinks']['chatBackData'];
    const chatBackDataExpired = !chatBackData?.expiryUTCTime ? true : new Date() > new Date(chatBackData.expiryUTCTime);
    const intentMessage = resourceContext.intentParams || undefined;
    const { config: vacConfig } = useVirtualAgentContext();
    const isNewContactUsEnabled = useExperiment('Contact_Us_Redesign_MVP_BEX_HCOM').exposure.bucket === 1;
    const isMultiVariantEnabled = useExperiment('Contact_Us_and_VA_FAB_on_Help_Center').exposure.bucket;

    const pageSpecificData = {
      experience: {
        page_name: egClickStreamEventPageName,
        app_name: egClickStreamEventAppName,
        app_version: egClickStreamEventVersion,
      },
    };
    React.useEffect(() => {
      if (sharedUIVacExposure.bucket === 1) {
        reset({
          data: pageSpecificData,
          config: {
            isEgClickstream: true,
          },
        });
      }
    }, [sharedUIVacExposure]);

    const trackVacLoadEvent = () => {
      const clickStreamEvent = prepareClickStreamPayload(
        virtualAgentControlStore,
        egClickStreamEventNames.virtualAgentInitiated,
        egClickStreamEventTypes.impression,
        egClickStreamEventCategories.virtualAgent
      );
      trackClick(clickStreamEvent);
    };

    const trackVacLoadFailEvent = () => {
      const clickStreamEvent = prepareClickStreamPayload(
        virtualAgentControlStore,
        egClickStreamEventNames.virtualAgentFailed,
        egClickStreamEventTypes.outcome,
        egClickStreamEventCategories.virtualAgent
      );
      trackClick(clickStreamEvent);
    };
    const handleVacEvents = (event, props) => {
      if (
        chatBackDataExpired &&
        !intentMessage?.intent &&
        event.type === vacEvents.vacFabPresented &&
        blankSlateLayout &&
        userHasTrip &&
        !isNewContactUsEnabled
      ) {
        if (isMultiVariantEnabled == 0) {
          setVacFrameVisibilityHidden(true);
        } else if (isMultiVariantEnabled == 1 || isMultiVariantEnabled == 3) {
          setVacFrameVisibilityHidden(false);
        }
      }

      if (event.type === vacConversationCreatedEvent) {
        const { conversationId, participantId } = event.payload;
        virtualAgentControlStore.setVirtualAgentControlIdentifiers(conversationId, participantId);
        trackVacLoadEvent();
      }

      if (event.type === vacEvents.vacInitializedEvent) {
        if (isNewContactUsEnabled) {
          sessionStorage.setItem('CHAT_WIDGET_OPEN_AFTER_LOGIN', 'true');
        }
        logExperiment(exposure);
      }
      // Using indexOf instead of includes for IE support.
      if (config?.successEvents.indexOf(event.type) !== -1) {
        logger.logEvent(EVENTS.VAC_SUCCESS_EVENT_RECEIVED, {
          message: `Success event received from VAC - ${event.type}`,
        });
        props.removeLoader();
      } else if (errorEvents.indexOf(event.type) !== -1) {
        const errorType = errorEventMap[event.type] || fallbackError;
        props.setError(errorType);
        logger.logEvent(EVENTS.VAC_ERROR_EVENT_RECEIVED, {
          message: `${loggingConstants.VAC_ERROR_EVENT_RECEIVED}: Error Event received from VAC - ${event.type}`,
        });
        trackVacLoadFailEvent();
      } else if (vacHandledErrorEvents.indexOf(event.type) != -1) {
        // For such VAC handled error events we don't need to show our custom error UI as VAC already has handling for these.
        props.removeLoader();

        logger.logEvent(EVENTS.VAC_ERROR_EVENT_RECEIVED, {
          message: `${loggingConstants.VAC_ERROR_EVENT_RECEIVED}: Error Event received from VAC - ${event.type}`,
        });
      } else {
        // This logs any non success/error events received if we are subscribed to them
        logger.logEvent(EVENTS.VAC_EVENT_RECEIVED, { message: `VAC event received - ${event.type}` });
      }
      return;
    };

    useEffect(() => {
      const timer = setTimeout(() => {
        if (window.VirtualAgentControl?.initialized && window.VacClientAdapter) {
          virtualAgentProps.removeLoader();
        }
      }, 3000);
      return () => clearTimeout(timer);
    }, []);

    if (isNewContactUsEnabled) {
      return (
        <ContactUs
          vacProps={{ ...embeddedVacProps, vacState: virtualAgentProps }}
          handleEvents={(event) => {
            handleVacEvents(event, virtualAgentProps);
            vacConfig.eventHandler(event);
          }}
          inputs={{}}
          intentMessage={!chatBackDataExpired ? {
            intent: chatBackData.intent,
            quickLinkId: chatBackData.quickLinkId,
            ...chatBackData.payload,
          } : intentMessage}
          conversationIdentifiers={{
            conversationId: chatBackDataExpired ? undefined : chatBackData.conversationId,
            participantId: chatBackDataExpired ? undefined : chatBackData.participantId,
          }}
        />
      );
    }

    return (
      <>
        {chatBackDataExpired ? (
          <Experiment name="Shared_UI_VAC_Web_HelpCenter">
            <ExperimentControl>
              <VirtualAgentControl
                applicationName="ecp-help-center"
                pageName="ecp-help-center"
                clientOverrides={{
                  subscribedEvents: [...subscribedEvents, ...REQUIRED_EVENT],
                  eventHandler: (event) => {
                    handleVacEvents(event, virtualAgentProps);
                    vacConfig.eventHandler(event);
                  },
                  enableAutoOpenChatWidget: signedInUser,
                  embedded: config.embedded,
                  containerElement: config.embedded ? document.getElementById('vac-container') : undefined,
                  configurationOverrides: {
                    hideHeader: config.hideHeader,
                    hideAvatar: config.hideAvatar,
                    textInputPlaceHolder: config.textInputPlaceHolder,
                  },
                  conversationProperties: {
                    skipWelcome: intentMessage ? 'true' : 'false',
                  },
                  intentMessage: intentMessage,
                }}
                inputs={{}}
                skipSsr
              />
            </ExperimentControl>
            <ExperimentVariant bucket={1}>
              <VacOrchestrator inputs={{}}>
                <Chat
                  autoScroll
                  inputs={{}}
                  modeOptions={{
                    mode: 'CREATE_CONVERSATION',
                    context: {
                      conversationType: 'MAIN',
                    },
                  }}
                />
              </VacOrchestrator>
            </ExperimentVariant>
          </Experiment>
        ) : (
          <Experiment name="Shared_UI_VAC_Web_HelpCenter">
            <ExperimentControl>
              <VirtualAgentControl
                applicationName="ecp-help-center"
                pageName="ecp-help-center"
                clientOverrides={{
                  subscribedEvents: [...subscribedEvents, ...REQUIRED_EVENT],
                  eventHandler: (event) => {
                    handleVacEvents(event, virtualAgentProps);
                    vacConfig.eventHandler(event);
                  },
                  enableAutoOpenChatWidget: true,
                  embedded: config.embedded,
                  containerElement: config.embedded ? document.getElementById('vac-container') : undefined,
                  configurationOverrides: {
                    hideHeader: config.hideHeader,
                    hideAvatar: config.hideAvatar,
                    textInputPlaceHolder: config.textInputPlaceHolder,
                  },
                  intentMessage: {
                    intent: chatBackData.intent,
                    quickLinkId: chatBackData.quickLinkId,
                    ...chatBackData.payload,
                  },
                  conversationProperties: {
                    skipWelcome: intentMessage ? 'true' : chatBackData?.intent ? 'true' : 'false',
                  },
                  mode: 'JOIN_CONVERSATION',
                  conversationId: chatBackData.conversationId,
                  participantId: chatBackData.participantId,
                }}
                inputs={{}}
                skipSsr
              />
            </ExperimentControl>
            <ExperimentVariant bucket={1}>
              <VacOrchestrator inputs={{}}>
                <Chat
                  autoScroll
                  inputs={{}}
                  modeOptions={{
                    mode: 'JOIN_CONVERSATION',
                    context: {
                      conversationType: 'MAIN',
                      conversationId: chatBackData.conversationId,
                      participantId: chatBackData.participantId,
                    },
                  }}
                />
              </VacOrchestrator>
            </ExperimentVariant>
          </Experiment>
        )}
      </>
    );
  }
);

export const VirtualAgentControlComponent: React.FC<VirtualAgentControlProps> = (props: VirtualAgentControlProps) => (
  <Viewport>
    <ViewSmall>
      <RenderVac config={floatingVacProps} virtualAgentProps={props} />
    </ViewSmall>
    <ViewMedium>
      <RenderVac config={embeddedVacProps} virtualAgentProps={props} />
    </ViewMedium>
  </Viewport>
);

VirtualAgentControlComponent.displayName = 'VirtualAgentControlComponent';
